import { useRecursiveOrganizationFilteredByKey } from '@/features/tenant-manager/organization/_hooks/organization';
import { useFilteredUsers } from '@/features/tenant-manager/user/_hooks/user';
import { useId } from '@/hooks';
import { isTargetYearWithinFiscalYearRange } from '@/utils';
import { useState } from 'react';
import type { z } from 'zod';
import { convertFormSchemaToRequestProjectMode } from '../_utils/format-project-data';
import type { ProjectModel } from '../projects/_models/project';
import type { ProjectFormSchema } from '../projects/forms/_model/project-form';
import { useProject } from './project';

type FormValues = z.infer<typeof ProjectFormSchema>;

/**
 * プロジェクトからユーザー・組織IDを取得するための引数の型定義
 * - form: フォーム入力データから取得
 * - initial: 初期データ（既存プロジェクト）から取得
 */
type getUserAndOrganizationIdsFromProjectArgs = (
  | { data: FormValues; type: 'form' }
  | {
      data: z.infer<typeof ProjectModel> | undefined;
      type: 'initial';
    }
) & { projectId: string };

/**
 * プロジェクトデータからユーザーIDと組織IDを抽出する
 */
const getUserAndOrganizationIdsFromProject = ({
  data,
  type,
  projectId,
}: getUserAndOrganizationIdsFromProjectArgs) => {
  const values =
    type === 'form'
      ? convertFormSchemaToRequestProjectMode({
          values: { id: projectId, ...data },
          type: 'update',
        })
      : data;

  // KSシートかつ標準型の場合のみ、(追加)情報を取得
  const additionalJurisdictionalUsers =
    values?.sheetType === 'KS' &&
    values.fundAdditionalAttributes?.fundSheetType === 'standard'
      ? values.fundAdditionalAttributes.additionalJurisdictionalUsers
      : undefined;

  const additionalJurisdictionalBusinessManagerIds =
    additionalJurisdictionalUsers?.flatMap((v) => v.businessManagerIds) ?? [];

  const additionalJurisdictionalPeopleInChargeIds =
    additionalJurisdictionalUsers?.flatMap((v) => v.peopleInChargeIds) ?? [];

  const additionalJurisdictionalOrganizationId =
    additionalJurisdictionalUsers?.flatMap((v) => v.organizationId ?? []) ?? [];

  // プロジェクトの各種組織IDを取得
  const organizationIds = values?.organizationId ? [values.organizationId] : [];
  const relatedOrganizationIds = values?.relatedOrganizationIds ?? [];

  const otherResponsibleOrganizationIds =
    values?.otherResponsibleOrganizationIds ?? [];

  // プロジェクトの担当者IDを取得
  const peopleInChargeIds = values?.peopleInChargeIds ?? [];
  const businessManagerIds = values?.businessManagerIds ?? [];

  // ssの場合のみ独立行政法人を取得
  const independentAdministrativeInstitutionIds =
    values?.sheetType === 'SS' && values?.independentAdministrativeAgencyId
      ? [values?.independentAdministrativeAgencyId]
      : [];

  // すべてのユーザーIDと組織IDを統合して返す
  return {
    userIds: [
      ...peopleInChargeIds,
      ...businessManagerIds,
      ...additionalJurisdictionalPeopleInChargeIds,
      ...additionalJurisdictionalBusinessManagerIds,
    ],
    organizationIds: [
      ...additionalJurisdictionalOrganizationId,
      ...organizationIds,
      ...relatedOrganizationIds,
      ...otherResponsibleOrganizationIds,
      ...independentAdministrativeInstitutionIds,
    ],
  };
};

/**
 * 配列内の各IDの出現回数をカウントする
 *
 * @param arr - ID文字列の配列
 * @returns IDをキー、出現回数を値とするオブジェクト
 */
const getCountMapById = (arr: string[]): Record<string, number> => {
  return arr.reduce<Record<string, number>>((acc, id) => {
    acc[id] = (acc[id] || 0) + 1;
    return acc;
  }, {});
};

/**
 * 存在しないユーザー・組織の削除を確認する
 * 参考: https://weldrow.slack.com/archives/C05454EH8PK/p1716351049215549?thread_ts=1715068196.468919&cid=C05454EH8PK
 */
const checkRemoveNotExistUserIdOrNotExistOrganizationId = async ({
  inputValues,
  notExistUserIds,
  notExistOrganizationIds,
  setOpenUpdateConfirmModal,
  projectId,
}: {
  inputValues: FormValues;
  notExistUserIds: string[];
  notExistOrganizationIds: string[];
  setOpenUpdateConfirmModal: (value: {
    isOpen: true;
    values: FormValues;
  }) => void;
  projectId: string;
}): Promise<{ isRemovedNotExistValue: boolean }> => {
  // 無効化ユーザー・除外組織がない場合は早期リターン
  if (!notExistUserIds?.length && !notExistOrganizationIds?.length) {
    return { isRemovedNotExistValue: false };
  }

  // フォーム入力からユーザー・組織IDを取得
  const { userIds: inputUserIds, organizationIds: inputOrganizationIds } =
    getUserAndOrganizationIdsFromProject({
      type: 'form',
      data: inputValues,
      projectId,
    });

  // 無効化ユーザーの参照が削除されたかチェック
  const isRemoveNotExistUser = notExistUserIds?.length
    ? Object.entries(getCountMapById(notExistUserIds)).some(
        ([userId, count]) =>
          inputUserIds.filter((v) => v === userId).length !== count,
      )
    : false;

  // 除外組織の参照が削除されたかチェック
  const isRemoveNotExistOrganization = notExistOrganizationIds?.length
    ? Object.entries(getCountMapById(notExistOrganizationIds)).some(
        ([organizationId, count]) =>
          inputOrganizationIds.filter((v) => v === organizationId).length !==
          count,
      )
    : false;

  // 無効化ユーザー・除外組織が検出された場合、確認モーダルを表示
  if (isRemoveNotExistUser || isRemoveNotExistOrganization) {
    setOpenUpdateConfirmModal({
      isOpen: true,
      values: inputValues,
    });
    return { isRemovedNotExistValue: true };
  }
  return { isRemovedNotExistValue: false };
};

/**
 * プロジェクト更新時(分割)時に以下を確認します。
 * - 無効化されたユーザーの参照が残っている場合、組織に１つも所属していないユーザーの場合
 * - 除外設定された組織または、終了年度を超過している組織の参照が残っている場合
 * 上記の場合、確認モーダルを表示してユーザーに通知します。
 */
export const useCheckRemoveNotExistUserIdOrNotExistOrganizationId = () => {
  const { id } = useId();
  const { data: projectData } = useProject(id);
  const [openUpdateConfirmModal, setOpenUpdateConfirmModal] = useState<
    | {
        isOpen: true;
        values: FormValues;
      }
    | { isOpen: false }
  >({ isOpen: false });

  // 初期データ（現在のプロジェクト）からユーザー・組織IDを取得
  const initialUserAndOrganization = getUserAndOrganizationIdsFromProject({
    type: 'initial',
    data: projectData,
    projectId: id,
  });

  // 無効化もしくは、組織に所属して良いないユーザーを検出するためなのでuseFilteredUsersを利用
  const { data: notExistUserIds } = useFilteredUsers({
    params: {
      filterBy: 'ids',
      ids: initialUserAndOrganization.userIds,
    },
    options: {
      ignoreNotJoinedOrganization: true,
    },
    enabled: !!initialUserAndOrganization.userIds.length,
    select: (data) => {
      const ids = data.results.map((d) => d.id);
      return initialUserAndOrganization.userIds.flatMap((v) =>
        ids.includes(v) ? [] : [v],
      );
    },
  });

  // 除外設定もしくは、終了年度を超過している組織IDを検出
  const { data: notExistOrganizationIds } =
    useRecursiveOrganizationFilteredByKey({
      params: {
        ids: initialUserAndOrganization.organizationIds,
      },
      options: {
        filterBy: 'all',
        separator: undefined,
        ignoreExcluded: true,
      },
      select: (data) => {
        const ids = data.flatMap((v) => {
          const isWithinFiscalYearRange = isTargetYearWithinFiscalYearRange({
            targetYear: projectData?.fiscalYear,
            startYear: v.startYear,
            endYear: v.endYear,
          });
          return isWithinFiscalYearRange ? [v.id] : [];
        });
        return initialUserAndOrganization.organizationIds.flatMap((v) =>
          ids.includes(v) ? [] : [v],
        );
      },
    });

  return {
    checkRemoveNotExistUserIdOrNotExistOrganizationId: (
      inputValues: FormValues,
    ) =>
      checkRemoveNotExistUserIdOrNotExistOrganizationId({
        inputValues,
        notExistUserIds: notExistUserIds ?? [],
        notExistOrganizationIds: notExistOrganizationIds ?? [],
        setOpenUpdateConfirmModal,
        projectId: id,
      }),
    onCancelModal: () => {
      setOpenUpdateConfirmModal({ isOpen: false });
    },
    openUpdateConfirmModal,
  };
};
